import { useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import type { OverlayKitProps } from '@/shared/forms/types';
import { cn } from '@/shared/utils/cn';

import { Button } from './Button';
import { ConfirmDiscard } from './ConfirmDiscard';
import { Icon } from './Icon';

export interface ModalProps extends OverlayKitProps {
  open: boolean;
  title: string;
  /** A meta line under the title in the dialog head — what the dialog is about. */
  subtitle?: ReactNode;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
  /** `lg` (760px) for a decision that collects a table — the partial approval (A-02). */
  size?: 'md' | 'lg';
}

/**
 * Native <dialog> modal.
 *
 * Using the platform element gives focus trapping, Escape-to-close and inert
 * background content without re-implementing any of it, and it inherits the
 * document direction so RTL needs no special handling.
 *
 * UI-3 (FORM-04): a dialog holding unsaved input (`dirty`) asks before Escape
 * or its head's Close throws the input away (ConfirmDiscard); one with a save
 * in flight (`busy`) is not dismissed until it settles. The caller's own
 * footer Cancel stays the caller's: it says what it does.
 */
export function Modal({
  open,
  title,
  subtitle,
  onClose,
  children,
  footer,
  size = 'md',
  dirty = false,
  busy = false,
}: ModalProps) {
  const { t } = useTranslation();
  const ref = useRef<HTMLDialogElement>(null);
  // One id per dialog: two mounted dialogs sharing `modal-title` gave the
  // second the first one's name.
  const titleId = useId();
  const [confirming, setConfirming] = useState(false);

  useEffect(() => {
    const dialog = ref.current;

    if (!dialog) return;

    if (open && !dialog.open) dialog.showModal();
    if (!open && dialog.open) dialog.close();
  }, [open]);

  /** Every way of dismissing it except the caller's own buttons. */
  const requestClose = () => {
    if (busy) return;
    if (dirty) {
      setConfirming(true);
      return;
    }
    onClose();
  };

  return (
    <>
      <dialog
        ref={ref}
        onCancel={(event) => {
          /*
           * React carries a nested dialog's `cancel` and `close` up the
           * component tree even though the platform does not bubble them: a
           * confirmation opened from inside this one must not close it too.
           */
          if (event.target !== event.currentTarget) return;
          event.preventDefault();
          requestClose();
        }}
        onClose={(event) => {
          if (event.target !== event.currentTarget) return;
          onClose();
        }}
        aria-labelledby={titleId}
        aria-busy={busy || undefined}
        className={cn(
          'm-auto rounded-xl bg-card p-0 text-ink backdrop:bg-[var(--overlay-scrim)]',
          size === 'lg' ? 'w-[min(47.5rem,calc(100vw-2rem))]' : 'w-[min(36rem,calc(100vw-2rem))]',
          /*
           * Addendum 04 §Overlays: on a phone a dialog is a bottom sheet — full
           * width, from the bottom edge, at most 88% high, its footer fixed so
           * the commit stays reachable. `open:` for the display, never a bare
           * `flex`: a closed <dialog> is hidden only by the browser's own
           * `display: none`, which an author display would override.
           */
          'max-sm:mt-auto max-sm:mb-0 max-sm:max-h-[88dvh] max-sm:w-full max-sm:max-w-none max-sm:rounded-b-none max-sm:open:flex max-sm:open:flex-col',
        )}
      >
        <div className="flex items-center justify-between border-b border-line-subtle px-5 py-4">
          <div className="min-w-0">
            <h2 id={titleId} className="text-base font-semibold wrap-break-word">
              {title}
            </h2>
            {subtitle ? <p className="type-meta mt-0.5 text-ink-muted">{subtitle}</p> : null}
          </div>
          {/*
            Named distinctly from any "Close" a dialog puts in its own footer. Two
            buttons whose accessible name is the same word, side by side in one
            dialog, is ambiguous to anybody navigating by name — which includes a
            screen-reader user and, as it turned out, a test.
          */}
          <Button
            variant="ghost"
            size="sm"
            onClick={requestClose}
            aria-label={t('a11y.closeDialog')}
            aria-disabled={busy || undefined}
          >
            <Icon name="close" size={16} />
          </Button>
        </div>

        {/*
          On a phone the dialog is a bottom sheet as tall as its content; its
          body starts from that content (`max-sm:flex-auto`), never a 0% basis,
          for the reason Sheet's body does (R-061 DEF-020 / DEF-025): the WebKit
          of Safari 17.4 to 26.0 squeezed this body to a sliver, its fields out
          of reach.
        */}
        <div className="max-h-[70vh] [--sticky-top-offset:0px] overflow-y-auto px-5 py-4 max-sm:max-h-none max-sm:min-h-0 max-sm:flex-auto">
          {children}
        </div>

        {footer ? (
          <div className="flex flex-wrap justify-end gap-2 border-t border-line-subtle px-5 py-4">
            {footer}
          </div>
        ) : null}
      </dialog>

      {dirty ? (
        <ConfirmDiscard
          open={open && confirming}
          onKeep={() => setConfirming(false)}
          onDiscard={() => {
            setConfirming(false);
            onClose();
          }}
        />
      ) : null}
    </>
  );
}
